import fp from 'lodash/fp'
import {
  combine,
  withContext,
  withCallbacks,
  withStateHandlers,
} from 'react-hooks-combine'
import { StoreContext } from 'store/context'
import { toaster } from 'utils/toast-wrapper'
import { showNotificationBasedOnDevice, redirectToProfileWithNotification } from 'utils/notification-helper'

import ProfileComponent from './component'

const mapToServerFormat = (changes, role) => {
  const {
    address1,
    address2,
    zip,
    country,
    state,
    city,
    firstName,
    lastName,
    nickName,
    beneficiaryName,
    taxId,
  } = changes

  const countryName = country ? country.name : undefined

  if (role === 'BENEFICIARY') {
    return {
      beneficiaryName,
      taxId,
      address: {
        address1,
        address2,
        zip,
        country: countryName,
        state,
        city,
      },
    }
  }

  return {
    firstName,
    lastName,
    nickName,
    taxId,
    address: {
      address1,
      address2,
      zip,
      country: countryName,
      state,
      city,
    },
  }
}

export default combine({
  hooks: [
    withStateHandlers(
      {
        loading: false,
      },
      {
        setState: ({ args: [state] }) => state,
      }
    ),
    withContext('loggedEntity', StoreContext, fp.get('auth.loggedEntity')),
    withContext('appConfig', StoreContext, ({ appConfig }) => appConfig),

    withCallbacks(
      {
        onUpdateProfile:
          ({ loggedEntity, setState }) =>
            async (changes) => {
              setState({ loading: true })
              try {
                const formattedData = mapToServerFormat(changes, loggedEntity.role)
                await loggedEntity.update(formattedData)
                setState({ loading: false })

                // Success scenario - use appropriate notification based on device
                showNotificationBasedOnDevice('success')

              } catch (error) {
                setState({ loading: false })

                // Error scenario - use appropriate notification based on device
                showNotificationBasedOnDevice('error', 'Changes unsaved')
              }
            },

        onChangePassword:
          ({ loggedEntity, setState }) =>
            async ({ oldPassword, newPassword }) => {
              setState({ loading: true })
              try {
                await loggedEntity.changePassword(oldPassword, newPassword)
                setState({ loading: false })

                // Success scenario - use appropriate notification based on device
                showNotificationBasedOnDevice('success')

              } catch (error) {
                setState({ loading: false })

                // Error scenario - use appropriate notification based on device
                showNotificationBasedOnDevice('error', 'Password change failed')
              }
            },

        onDeleteAccount:
          ({ loggedEntity, setState }) =>
            async () => {
              // Only mobile has delete account functionality
              const isMobile = window.innerWidth <= 767
              if (!isMobile) return

              try {
                setState({ loading: true })
                await loggedEntity.deleteProfile()
                setState({ loading: false })

                // Mobile: Show notification and redirect
                redirectToProfileWithNotification(null, 'success', 'Account deleted successfully', true)

                // After successful deletion, sign out the user
                setTimeout(() => {
                  loggedEntity.getRoot().auth.signout()
                }, 2000)

              } catch (error) {
                setState({ loading: false })

                // Mobile: Show notification and redirect to profile
                redirectToProfileWithNotification(null, 'error', 'Account deletion failed', true)
              }
            },
      },
      []
    ),
  ],

  transformProps: ({
    loggedEntity,
    onUpdateProfile,
    onChangePassword,
    onDeleteAccount,
    loading,
    appConfig,
  }) => ({
    onUpdateProfile,
    onChangePassword,
    onDeleteAccount,
    entity: loggedEntity,
    isLoading: loading,
    appConfig,
  }),
})(ProfileComponent)
